/**
 * The hover card's client cache (issue 73).
 *
 * A hover preview trades one page fetch for many small ones, so done naively
 * it INCREASES database load. Three things keep that from happening, and this
 * module owns two of them (the ~150ms hover intent is the layer's):
 *
 *   · a session cache keyed by system, stack, segment and slug — re-hovering
 *     the same link is free, and a list of 20 rows settles to at most 20
 *     lookups however much the reader moves around;
 *   · batching — `warm` takes every slug a surface shows and asks for the ones
 *     the cache does not hold in ONE request, so a list page, a similar rail
 *     or a detail page's reference links cost one round trip, not one each.
 *
 * A slug the server did not answer is cached as `null`, so a dead link is
 * asked about once. The cache is per page load: a system switch reloads the
 * page, and the stack rides in the key.
 */

import apiClient from '../config/axios';
import { getActiveSystem } from '../config/system';
import { getStackKey } from '../config/stack';

// A batch this size is what the server caps a request at.
const MAX_BATCH = 100;

const cache = new Map(); // key -> preview | null
const pending = new Map(); // key -> Promise<preview | null>

const keyOf = (segment, slug) => `${getActiveSystem()}${getStackKey()}|${segment}|${slug}`;

/** The cached preview, `null` for a known miss, `undefined` when unknown. */
export function peekPreview(segment, slug) {
  return cache.get(keyOf(segment, slug));
}

/**
 * Fetch the previews for `slugs` the cache does not hold, in one request per
 * hundred, and resolve to a map of slug -> preview | null for all of them.
 * `summary` asks the server for a short plain-text summary of each entry's
 * description (issue 1017); the hover layer asks it of a family whose card has
 * no rows, the same for every slug of that segment, so the cache key needs no
 * flag.
 */
export function warmPreviews(segment, slugs, { summary = false } = {}) {
  const wanted = [...new Set((slugs || []).filter(Boolean))];
  const misses = wanted.filter((slug) => {
    const key = keyOf(segment, slug);
    return !cache.has(key) && !pending.has(key);
  });
  for (let i = 0; i < misses.length; i += MAX_BATCH) {
    const chunk = misses.slice(i, i + MAX_BATCH);
    const p = apiClient
      .get(`/api/${segment}/previews`, { params: { slugs: chunk.join(','), ...(summary ? { summary: 1 } : {}) } })
      .then((res) => {
        const bySlug = new Map((res.data?.previews || []).map((row) => [row.slug, row]));
        for (const slug of chunk) cache.set(keyOf(segment, slug), bySlug.get(slug) || null);
      })
      .catch(() => {
        // A failed batch is forgotten rather than cached: the next hover asks
        // again, and a transient error does not silence a link for the session.
      })
      .finally(() => { for (const slug of chunk) pending.delete(keyOf(segment, slug)); });
    for (const slug of chunk) pending.set(keyOf(segment, slug), p);
  }
  return Promise.all(wanted.map((slug) => pending.get(keyOf(segment, slug)) || Promise.resolve()))
    .then(() => Object.fromEntries(wanted.map((slug) => [slug, cache.get(keyOf(segment, slug)) ?? null])));
}

/** One entry's preview, from the cache or the network. */
export function getPreview(segment, slug, opts) {
  const key = keyOf(segment, slug);
  if (cache.has(key)) return Promise.resolve(cache.get(key));
  return warmPreviews(segment, [slug], opts).then((map) => map[slug] ?? null);
}

/** For tests. */
export function __resetPreviewCache() {
  cache.clear();
  pending.clear();
}
